import { LANGUAGES, type Language } from '@eformulieren/engine'
import type { ReactNode } from 'react'

/**
 * De taalkeuze van de invuller (R-144).
 *
 * **Taal is een weergavekeuze en niets meer.** De vragen en de antwoordstructuur
 * blijven dezelfde, en er gaat niets verloren bij het wisselen: elk zichtbaar
 * tekstelement in de definitie is een paar {nl, en} (R-143), de antwoorden staan op
 * stabiele veld-id's, en van een keuzeoptie wordt de `waarde` bewaard en nooit het
 * label. Daarom kan deze knop niets stukmaken, en daarom staat de taal nergens in
 * het document.
 *
 * Twee knoppen met `aria-pressed` en geen dropdown: het zijn twee talen (U9), de
 * huidige is meteen te zien, en er is geen keuzelijst nodig die eerst open moet.
 */
export interface LanguageSwitchProps {
  readonly taal: Language
  readonly onLanguage: (language: Language) => void
  /** Het label van de groep, in de taal die nu geldt. */
  readonly label?: string
  /**
   * De smalle vorm voor de kopbalk van het formulier: zichtbaar de taalcode ("NL", "EN") in één
   * omkaderde groep, zoals het ontwerp van het invulscherm (`Main.dc.html`) hem toont.
   *
   * De toegankelijke naam blijft de volle taalnaam: een schermlezer zegt "English" en niet "E N",
   * en de code is de gangbare afkorting van precies die naam. De inlog- en uitnodigingspagina
   * houden de brede vorm, want daar staat de wissel alleen en is er ruimte.
   */
  readonly compact?: boolean
}

const NAME: Record<Language, string> = { nl: 'Nederlands', en: 'English' }
const CODE: Record<Language, string> = { nl: 'NL', en: 'EN' }

export function LanguageSwitch({ taal: language, onLanguage, label, compact = false }: LanguageSwitchProps): ReactNode {
  return (
    <div
      className={compact ? 'ef-taalwissel ef-taalwissel--compact' : 'ef-taalwissel'}
      role="group"
      aria-label={label ?? (language === 'nl' ? 'Taal' : 'Language')}
    >
      {LANGUAGES.map((choice) => (
        <button
          type="button"
          key={choice}
          className="ef-taalwissel__knop"
          aria-pressed={choice === language}
          lang={choice}
          {...(compact ? { 'aria-label': NAME[choice] } : {})}
          onClick={() => onLanguage(choice)}
        >
          {compact ? CODE[choice] : NAME[choice]}
        </button>
      ))}
    </div>
  )
}
